import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import classNames from 'classnames';

import { Props } from './modal.types';

export const Modal = ({
  hasMobileHandle,
  isOpen,
  handleClose,
  variant = 'lg',
  children,
  maxWidthClass,
  beforeEnter,
  afterEnter,
  beforeLeave,
  afterLeave,
  enter = '',
  borderRadiusContainerClassName = 'rounded-t-2xl sm:rounded-2xl',
}: Props) => {
  const [startY, setStartY] = useState(0);
  const [currentTranslateY, setCurrentTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrentTranslateY(0);
      setIsDragging(false);
      setIsClosing(false);
    }
  }, [isOpen]);

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setStartY(touch.clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const currentY = touch.clientY;
    const diffY = currentY - startY;

    if (diffY > 0) {
      setCurrentTranslateY(diffY);
    }
  };

  const initiateClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      handleClose();
      setIsClosing(false);
    }, 300);
  };

  const handleTouchEnd = () => {
    if (currentTranslateY > 100) {
      initiateClose();
    } else {
      setCurrentTranslateY(0);
    }
    setIsDragging(false);
  };

  return (
    <Transition
      enter={enter}
      show={isOpen && !isClosing}
      as={Fragment}
      appear={true}
      beforeEnter={beforeEnter}
      afterEnter={afterEnter}
      beforeLeave={beforeLeave}
      afterLeave={afterLeave}>
      <Dialog className='relative z-40' open={isOpen} onClose={initiateClose} aria-label='modal'>
        <TransitionChild
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-300'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div
            className='fixed inset-0 transition-all backdrop-blur-xs dark:bg-transparent-12 bg-transparent-30'
            aria-hidden='true'
          />
        </TransitionChild>
        <TransitionChild
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='translate-y-full'
          enterTo='translate-y-0'
          leave='ease-in duration-300'
          leaveFrom='translate-y-0'
          leaveTo='translate-y-full'>
          <div className='fixed inset-0 flex items-end w-screen sm:items-center sm:justify-center'>
            <DialogPanel
              className={classNames(
                'bg-primary relative w-full sm:w-10/12 h-auto max-h-[95dvh] overflow-auto scrollbar scrollbar-hide',
                'sm:align-middle lg:my-8',
                'shadow-[2px_2px_20px_0_rgba(0,0,0,0.15)]',
                borderRadiusContainerClassName,
                {
                  'lg:max-w-211.5': variant === 'lg' && !maxWidthClass,
                  'lg:max-w-(--breakpoint-md)': variant === 'md' && !maxWidthClass,
                  'lg:max-w-(--breakpoint-sm)': variant === 'sm' && !maxWidthClass,
                  [`${maxWidthClass}`]: !!maxWidthClass,
                }
              )}
              style={{
                transform: `translateY(${currentTranslateY}px)`,
                transition: isDragging ? 'none' : 'transform 0.3s ease-in-out',
              }}>
              {hasMobileHandle && (
                <button
                  className='w-10 h-1 bg-transparent-20 rounded-full mx-auto mt-4 cursor-pointer block'
                  onClick={initiateClose}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                />
              )}
              {children}
            </DialogPanel>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};
